import type { IntercomUserType } from './IntercomUserType';
import type { OrgType } from './advisor-questionnaire/types';
import {
  EDWARD_JONES_ORGANIZATION_DISPLAY_NAME,
  EDWARD_JONES_ORGANIZATION_NAME,
  GENWORTH_ORGANIZATION_DISPLAY_NAME,
  GENWORTH_ORGANIZATION_NAME,
  NEW_YORK_LIFE_ORGANIZATION_DISPLAY_NAME,
  NEW_YORK_LIFE_ORGANIZATION_NAME,
} from './constants';

/**
 * A capability a partner program can withhold. Each value names one surface a
 * pilot advisor may be kept out of, and is passed to `<PilotRestricted>` at the
 * point the surface is rendered.
 */
export type PilotCapability =
  | 'add-partner'
  | 'anonymous-pui'
  | 'automatic-client-results'
  | 'carrier-bga'
  | 'client-reminder'
  | 'crm-integration'
  | 'distribution-partners'
  | 'email-connection'
  | 'engagement-campaigns'
  | 'explore-quotes'
  | 'intake-form'
  | 'invite-client'
  | 'logo-branding'
  | 'org-type'
  | 'policies-comparison'
  | 'quick-start'
  | 'quote-apply';

/** Copy for a capability that takes over a whole page rather than one control. */
export interface PilotBlockedPageCopy {
  heading: string;
  body: string;
}

/**
 * Replaces the generic "Fill Intake Form" surface with a self-plan form that records a
 * compliance acknowledgement. An optional `flagKey` gates the form separately from the
 * program flag; without one it is on for every advisor in the program.
 */
export interface PilotSelfPlanIntakeForm {
  flagKey?: FeatureFlagKeys;
  acknowledgementText: string;
  /** Identifies the acknowledgement in the ConsentRecords audit trail. */
  consentId: string;
}

/**
 * Requires a customer's own permission before an agent may enter that customer's information.
 *
 * Holds the partner-approved statements and the consent ids that pin them to the audit trail.
 * Deliberately has NO `flagKey`, unlike `PilotSelfPlanIntakeForm`: the feature is on exactly when
 * the advisor carries the program flag, and turning that off is the whole rollout control.
 */
export interface PilotAgentEnteredIntake {
  permissionText: string;
  acknowledgementText: string;
  /** Identifies the permission statement in the ConsentRecords audit trail. */
  permissionConsentId: string;
  /** Identifies the acknowledgement statement in the ConsentRecords audit trail. */
  acknowledgementConsentId: string;
  /** Re-opened behind the consent gate. The raw surface stays withheld. */
  reopensCapabilities?: readonly PilotCapability[];
}

export interface PilotProgram {
  flagKey: FeatureFlagKeys;
  organizationDisplayName: string;
  organizationName: string;
  /** Fills the "… is preset for {label}" tooltip on locked organization fields. */
  lockedOrgLabel: string;
  /**
   * Preset organization type for the program's advisors, filled in during onboarding
   * instead of being asked. Whether they may then change it is a separate question,
   * answered by `org-type` appearing in `restrictedCapabilities`.
   */
  lockedOrgType?: OrgType;
  intercomAdvisorType?: IntercomUserType;
  intercomClientType?: IntercomUserType;
  /** Withheld capabilities. Empty means the program pins identity only. */
  restrictedCapabilities: readonly PilotCapability[];
  restrictedTooltip: string;
  blockedPages?: Partial<Record<PilotCapability, PilotBlockedPageCopy>>;
  /** Advisor onboarding steps the program's advisors never see. */
  excludedOnboardingSteps?: readonly AdvisorOnboardingUIStep[];
  /**
   * Onboarding requirements the program excuses, for steps its advisors cannot satisfy.
   *
   * Read by the server when computing completion AND by the onboarding wizard when deciding
   * whether the step can be skipped, so the two cannot disagree about whether an advisor is
   * allowed to finish.
   */
  waivedOnboardingSteps?: readonly AdvisorOnboardingTrackingStep[];
  selfPlanIntakeForm?: PilotSelfPlanIntakeForm;
  agentEnteredIntake?: PilotAgentEnteredIntake;
  /**
   * Refuse withheld capabilities at the API as well as hiding them in the UI.
   *
   * Opt-in per program rather than on for all of them. Hiding a control and refusing
   * the request behind it are different guarantees, and only a program that committed
   * to the stronger one should pay for it: turning this on for a program that has been
   * running on UI gating alone would start rejecting calls its advisors can make today.
   */
  enforceRestrictionsAtApi?: boolean;
}

export const NYL_DEFAULT_HEALTHCARE_INFLATION_PERCENT = 3;
export const NYL_DEFAULT_HEALTHCARE_INFLATION_SOURCE_TEXT =
  '2023 Center for Medicare & Medicaid Services (CMS) Office of the Actuary, National Health Expenditure Projections';
export const NYL_DEFAULT_HEALTHCARE_INFLATION_SOURCE_URL =
  'https://www.cms.gov/newsroom/press-releases/cms-office-actuary-releases-2022-2031-national-health-expenditure-projections';
export const NYL_DEFAULT_CLIENT_INTAKE_GUIDE: ClientIntakeGuideType = 'none';
export const NYL_PILOT_ADVISOR_FEATURE_FLAG = 'nylPilotAdvisor' satisfies FeatureFlagKeys;

const QUOTE_APPLY_BLOCKED_BODY =
  'To evaluate insurance options during the pilot, follow your standard processes outside of Waterlily.';

const CRM_BLOCKED_BODY =
  'To keep client records in sync during the pilot, continue using your existing CRM workflow outside of Waterlily.';

/**
 * Every partner program, in precedence order: an advisor carrying more than one
 * flag resolves to the first match here, for organization identity, Intercom
 * segmentation and capability restrictions alike.
 */
export const PILOT_PROGRAMS: readonly PilotProgram[] = [
  {
    flagKey: 'edjPilotAdvisor',
    organizationDisplayName: EDWARD_JONES_ORGANIZATION_DISPLAY_NAME,
    organizationName: EDWARD_JONES_ORGANIZATION_NAME,
    lockedOrgLabel: 'the Edward Jones pilot',
    lockedOrgType: 'broker-dealer',
    intercomAdvisorType: 'edj_advisor',
    intercomClientType: 'edj_client',
    restrictedCapabilities: [
      'add-partner',
      'anonymous-pui',
      'client-reminder',
      'crm-integration',
      'email-connection',
      'explore-quotes',
      'intake-form',
      'invite-client',
      // Clients of a pilot advisor see the Edward Jones name, never a logo the advisor
      // uploaded themselves. Withholds the logo upload and the client-header branding
      // choice; the whitelabel organization name stays, pinned to the program's own.
      'logo-branding',
      'org-type',
      'policies-comparison',
      'quick-start',
      'quote-apply',
    ],
    restrictedTooltip:
      'This feature is available on Waterlily but is not part of the Edward Jones pilot program.',
    blockedPages: {
      'crm-integration': {
        heading:
          'This feature is available on Waterlily but is not part of the Edward Jones pilot program.',
        body: CRM_BLOCKED_BODY,
      },
      'quote-apply': {
        heading: 'Quote & Apply is not available for Edward Jones pilot advisors',
        body: QUOTE_APPLY_BLOCKED_BODY,
      },
    },
    excludedOnboardingSteps: ['connect-email', 'consent'],
    waivedOnboardingSteps: ['consent', 'invite-client'],
    selfPlanIntakeForm: {
      flagKey: 'edjAdvisorPilotIntakeForm',
      acknowledgementText:
        'By using this service, I acknowledge that I do so solely as a Waterlily client for the purpose of assessing my own needs, and not in my capacity as an Edward Jones Financial Advisor or associate.',
      consentId: 'advisorEdjSelfPlanAcknowledgement',
    },
  },
  {
    flagKey: 'genworthMode',
    organizationDisplayName: GENWORTH_ORGANIZATION_DISPLAY_NAME,
    organizationName: GENWORTH_ORGANIZATION_NAME,
    lockedOrgLabel: 'Genworth',
    waivedOnboardingSteps: ['activate-seat', 'consent', 'invite-client'],
    restrictedCapabilities: [],
    restrictedTooltip:
      'This feature is available on Waterlily but is not part of the Genworth experience.',
  },
  {
    flagKey: NYL_PILOT_ADVISOR_FEATURE_FLAG,
    organizationDisplayName: NEW_YORK_LIFE_ORGANIZATION_DISPLAY_NAME,
    organizationName: NEW_YORK_LIFE_ORGANIZATION_NAME,
    lockedOrgLabel: 'the New York Life pilot',
    lockedOrgType: 'broker-dealer',
    intercomAdvisorType: 'nyl_advisor',
    intercomClientType: 'nyl_client',
    // New York Life withholds product recommendations, illustrations, underwriting
    // guidance, application functionality, production integrations and client edit
    // rights for the duration of the pilot. Its advisors all onboard under the one
    // organization, so the organization type is pinned rather than asked.
    restrictedCapabilities: [
      'anonymous-pui',
      'automatic-client-results',
      'carrier-bga',
      'crm-integration',
      'distribution-partners',
      'email-connection',
      'engagement-campaigns',
      'explore-quotes',
      'intake-form',
      'org-type',
      'policies-comparison',
      'quote-apply',
    ],
    restrictedTooltip:
      'This feature is available on Waterlily but is not part of the New York Life pilot program.',
    blockedPages: {
      'quote-apply': {
        heading: 'Quote & Apply is not available for New York Life pilot advisors',
        body: QUOTE_APPLY_BLOCKED_BODY,
      },
      'crm-integration': {
        heading: 'CRM integrations are not available for New York Life pilot advisors',
        body: CRM_BLOCKED_BODY,
      },
    },
    excludedOnboardingSteps: ['connect-email'],
    // The pilot's first-plan options cannot produce a client on their own, so requiring one
    // would leave these advisors unable to finish onboarding at all.
    waivedOnboardingSteps: ['invite-client'],
    // New York Life committed that none of the withheld functionality is accessible,
    // which a UI-only control cannot promise: a browser holding a pre-deploy bundle
    // still renders every entry point.
    enforceRestrictionsAtApi: true,
    selfPlanIntakeForm: {
      acknowledgementText:
        'By using this service, I acknowledge that I do so solely as a Waterlily client for the purpose of assessing my own needs, and not in my capacity as a New York Life agent or associate.',
      consentId: 'advisorNylSelfPlanAcknowledgement',
    },
    agentEnteredIntake: {
      // TODO(nyl-legal): replace both statements with the approved wording before any advisor
      // carries this flag in a deployed environment. An advisor already in the pilot would show
      // these to a real customer the moment the code ships. The matching rows in
      // consents.json / seed-consents.sql / database-schema.sql must be updated in the same
      // change, since those are what the audit trail pins the version to.
      permissionText:
        'TODO(nyl-legal): I give my agent permission to enter my information into Waterlily on my behalf.',
      acknowledgementText:
        'TODO(nyl-legal): I acknowledge that the information my agent enters will be used to produce my long-term care plan, and that I am responsible for confirming it is accurate.',
      permissionConsentId: 'clientAgentEnteredPermission',
      acknowledgementConsentId: 'clientAgentEnteredAcknowledgement',
      reopensCapabilities: ['intake-form'],
    },
  },
];

/** The program an advisor belongs to, or null when they belong to none. */
export function findActivePilotProgram(
  featureFlags: FeatureFlags | undefined | null,
): PilotProgram | null {
  if (!featureFlags) {
    return null;
  }

  return PILOT_PROGRAMS.find(program => featureFlags[program.flagKey]) ?? null;
}

export function isNylPilotAdvisor(featureFlags: FeatureFlags | undefined | null): boolean {
  return findActivePilotProgram(featureFlags)?.flagKey === NYL_PILOT_ADVISOR_FEATURE_FLAG;
}

export function isPilotCapabilityRestricted(
  featureFlags: FeatureFlags | undefined | null,
  capability: PilotCapability,
): boolean {
  return !!findActivePilotProgram(featureFlags)?.restrictedCapabilities.includes(capability);
}

/**
 * Whether a request for this capability should be refused outright, rather than merely
 * hidden in the UI. True only for programs that opted into API enforcement.
 */
export function isPilotCapabilityRefusedAtApi(
  featureFlags: FeatureFlags | undefined | null,
  capability: PilotCapability,
): boolean {
  const program = findActivePilotProgram(featureFlags);

  return !!program?.enforceRestrictionsAtApi && program.restrictedCapabilities.includes(capability);
}

/** Onboarding requirements the advisor's program excuses. Empty when they belong to none. */
export function getPilotWaivedOnboardingSteps(
  featureFlags: FeatureFlags | undefined | null,
): readonly AdvisorOnboardingTrackingStep[] {
  return findActivePilotProgram(featureFlags)?.waivedOnboardingSteps ?? [];
}

/**
 * Whether the advisor's program excuses creating a first client.
 *
 * Gates the onboarding wizard's skip affordances. Without it the wizard demands a client the
 * program's own restrictions prevent the advisor from creating.
 */
export function isPilotFirstPlanWaived(featureFlags: FeatureFlags | undefined | null): boolean {
  return getPilotWaivedOnboardingSteps(featureFlags).includes('invite-client');
}

/** Onboarding steps the advisor's program waives. Empty when they belong to none. */
export function getPilotExcludedOnboardingSteps(
  featureFlags: FeatureFlags | undefined | null,
): readonly AdvisorOnboardingUIStep[] {
  return findActivePilotProgram(featureFlags)?.excludedOnboardingSteps ?? [];
}

/**
 * The self-plan intake form for this advisor, or null when their program has none or
 * the form's own gating flag is off.
 */
export function getPilotSelfPlanIntakeForm(
  featureFlags: FeatureFlags | undefined | null,
): PilotSelfPlanIntakeForm | null {
  const selfPlanIntakeForm = findActivePilotProgram(featureFlags)?.selfPlanIntakeForm;

  if (!selfPlanIntakeForm) {
    return null;
  }

  if (selfPlanIntakeForm.flagKey && !featureFlags?.[selfPlanIntakeForm.flagKey]) {
    return null;
  }

  return selfPlanIntakeForm;
}

/**
 * The only question the feature asks anywhere: non-null means this advisor may initiate an
 * agent-entered case. There is no separate flag.
 */
export function getPilotAgentEnteredIntake(
  featureFlags: FeatureFlags | undefined | null,
): PilotAgentEnteredIntake | null {
  return findActivePilotProgram(featureFlags)?.agentEnteredIntake ?? null;
}

/**
 * Distinct from `isPilotCapabilityRestricted`, which stays true — only the consented variant is
 * reachable. Callers showing the consented entry point ask this one.
 */
export function isPilotCapabilityReopenedByConsent(
  featureFlags: FeatureFlags | undefined | null,
  capability: PilotCapability,
): boolean {
  return !!getPilotAgentEnteredIntake(featureFlags)?.reopensCapabilities?.includes(capability);
}

/**
 * Partner programs that lock the advisor's organization name to a fixed value
 * (preset and non-editable during onboarding). Returns the locked names, or null
 * when the advisor's flags don't pin them to a partner organization.
 */
export function getLockedAdvisorOrgNames(
  featureFlags: FeatureFlags | undefined | null,
): { organizationDisplayName: string; organizationName: string } | null {
  const program = findActivePilotProgram(featureFlags);

  if (!program) {
    return null;
  }

  return {
    organizationDisplayName: program.organizationDisplayName,
    organizationName: program.organizationName,
  };
}

/**
 * The organization type preset by the advisor's program, or null when their flags don't
 * pin one. Callers use it both to prefill the onboarding field and to decide what to
 * persist, so a program's advisors never save a type they were never asked for.
 */
export function getLockedAdvisorOrgType(
  featureFlags: FeatureFlags | undefined | null,
): OrgType | null {
  return findActivePilotProgram(featureFlags)?.lockedOrgType ?? null;
}

export const PILOT_RELATIONSHIP_ACCESS_TYPES = [
  'no_client_access',
  'two_way',
  'new_user_sees_related_clients',
  'related_user_sees_new_user_clients',
] as const;

export const PILOT_PROGRAM_FEATURE_FLAG_KEYS = [
  'wizardMode',
  'vbShop',
  'transamerica',
  'fortifiedLife',
  'newYorkLife',
  'ltciPartners',
  'agis',
  'shopForInsurance',
  'quoteApplyCareScout',
  'quoteApplyLincolnFinancial',
  'quoteApplyEquiTrust',
  'quoteApplyNewYorkLife',
  'traditionalLTCPaymentOptions',
  'hideSinglePayAnd10Pay',
  'licensesManagement',
  'd2cMode',
  'edjPilotAdvisor',
  'edjAdvisorPilotIntakeForm',
  'genworthMode',
  'nylPilotAdvisor',
  'ccrcTypeAProducts',
  'financialFeasibility',
  'freemiumAdvisor',
  'quoteApplyManhattanLife',
  'twoFactorAuth',
] as const satisfies readonly FeatureFlagKeys[];

export type PilotRelationshipAccessType = (typeof PILOT_RELATIONSHIP_ACCESS_TYPES)[number];

export type PilotRelationshipKind = 'default_supervisor' | 'associated_staff';

export interface PilotOnboardingProgramRoleConfiguration {
  key: string;
  label: string;
  shortLabel: string;
  defaultSubscriptionOwnerEmail: string;
  featureFlags?: FeatureFlags;
  isActive: boolean;
  sortOrder: number;
}

export interface PilotOnboardingProgramSupervisorConfiguration {
  email: string;
  accessType: PilotRelationshipAccessType;
  isActive: boolean;
  sortOrder: number;
}

export interface PilotOnboardingProgramConfiguration {
  featureFlags: FeatureFlags;
  defaultSeatExpiresDateTime: string | null;
  roles: PilotOnboardingProgramRoleConfiguration[];
  defaultSupervisors: PilotOnboardingProgramSupervisorConfiguration[];
}

/** Role-level selections are authoritative once present. */
export function resolvePilotRoleFeatureFlags(
  programFeatureFlags: FeatureFlags,
  roleFeatureFlags?: FeatureFlags,
): FeatureFlags {
  if (roleFeatureFlags === undefined) return { ...programFeatureFlags };
  return { ...roleFeatureFlags };
}

export interface PilotOnboardingRelationshipSelection {
  relatedAdvisorId: string | null;
  relatedAdvisorEmail: string;
  kind: PilotRelationshipKind;
  accessType: PilotRelationshipAccessType;
}

export interface PilotOnboardingRequestSnapshot {
  role: PilotOnboardingProgramRoleConfiguration;
  featureFlags: FeatureFlags;
  relationships: PilotOnboardingRelationshipSelection[];
}

export interface PreRegisteredFeatureFlagArtifact {
  target: 'pre_registered_feature_flags';
  id: string;
  created: boolean;
  previousFeatureFlags?: FeatureFlags;
  appliedFeatureFlags: FeatureFlags;
}

export interface PreRegisteredRelationshipArtifact {
  target: 'pre_registered_team_relationship';
  id: string;
  created: boolean;
  previousAccessMode?: ClientAccessMode;
  previousEnabledClientAccess?: boolean;
  appliedAccessMode: ClientAccessMode;
}

export interface AdvisorHierarchyArtifact {
  target: 'advisor_hierarchy';
  supervisorAdvisorId: string;
  subordinateAdvisorId: string;
  created: boolean;
  previousIsActive?: boolean;
  previousAccessMode?: ClientAccessMode;
  previousEnabledClientAccess?: boolean;
  appliedAccessMode: ClientAccessMode;
}

export type PilotOnboardingArtifact =
  | PreRegisteredFeatureFlagArtifact
  | PreRegisteredRelationshipArtifact
  | AdvisorHierarchyArtifact;

export type PilotOnboardingArtifactTarget = PilotOnboardingArtifact['target'];

export function isPilotOnboardingArtifact(value: unknown): value is PilotOnboardingArtifact {
  if (!value || typeof value !== 'object') return false;
  const artifact = value as Record<string, unknown>;
  if (typeof artifact.created !== 'boolean') return false;

  switch (artifact.target) {
    case 'pre_registered_feature_flags':
      return (
        typeof artifact.id === 'string' &&
        !!artifact.appliedFeatureFlags &&
        typeof artifact.appliedFeatureFlags === 'object'
      );
    case 'pre_registered_team_relationship':
      return typeof artifact.id === 'string' && typeof artifact.appliedAccessMode === 'string';
    case 'advisor_hierarchy':
      return (
        typeof artifact.supervisorAdvisorId === 'string' &&
        typeof artifact.subordinateAdvisorId === 'string' &&
        typeof artifact.appliedAccessMode === 'string'
      );
    default:
      return false;
  }
}

export function selectPilotOnboardingArtifactsByTarget<
  TTarget extends PilotOnboardingArtifactTarget,
>(
  artifacts: Record<string, unknown> | undefined,
  target: TTarget,
): Array<Extract<PilotOnboardingArtifact, { target: TTarget }>> {
  if (!artifacts) return [];
  const rawRecords = Array.isArray(artifacts.records)
    ? artifacts.records
    : isPilotOnboardingArtifact(artifacts)
      ? [artifacts]
      : [];
  return rawRecords.filter(
    (artifact): artifact is Extract<PilotOnboardingArtifact, { target: TTarget }> =>
      isPilotOnboardingArtifact(artifact) && artifact.target === target,
  );
}

export type PilotOnboardingSource = 'pilot_onboarding' | 'legacy';

export type PilotOnboardingStatus =
  | 'pending'
  | 'processing'
  | 'needs_attention'
  | 'invited'
  | 'active'
  | 'declined'
  | 'expired'
  | 'deleted';

export const PILOT_ONBOARDING_PROCESSING_LEASE_MS = 5 * 60 * 1000;

export type PilotOnboardingAuditAction =
  | 'created'
  | 'retry_requested'
  | 'invitation_resent'
  | 'edited'
  | 'replacement_requested'
  | 'deleted'
  | 'accepted'
  | 'declined';

export interface PilotOnboardingAuditEvent {
  action: PilotOnboardingAuditAction;
  actorAdvisorId: string | null;
  at: string;
  details?: Record<string, unknown>;
}

export type PilotOnboardingStepKey = 'flags' | 'relationships' | 'seat_invitation';

export type PilotOnboardingStepStatus = 'pending' | 'processing' | 'succeeded' | 'failed';

export interface PilotOnboardingStepState {
  status: PilotOnboardingStepStatus;
  attempts: number;
  errorCode?: string;
  errorStage?: string;
  errorDetails?: Record<string, unknown>;
  artifacts?: Record<string, unknown>;
  updatedAt?: string;
}

export type PilotOnboardingSteps = Record<PilotOnboardingStepKey, PilotOnboardingStepState>;

export const EMPTY_PILOT_ONBOARDING_STEPS: PilotOnboardingSteps = {
  flags: { status: 'pending', attempts: 0 },
  relationships: { status: 'pending', attempts: 0 },
  seat_invitation: { status: 'pending', attempts: 0 },
};
